import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "@/app/providers/AppContext";
import type { TickerSuggestion } from "@/shared/lib/types";
import { getTimezone, getTimeFormat } from "@/shared/lib/timezone";
import BrandLogo from "@/shared/components/BrandLogo";
import { Mono, StatDot, T } from "@/shared/ui/DesignLib";

interface TickerSearchResponse {
  results?: TickerSuggestion[];
}

const AppHeader = () => {
  const [time, setTime] = useState(new Date());
  const { profile, api } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Ticker search state
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [dynamicTickers, setDynamicTickers] = useState<TickerSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const search = async () => {
      const q = query.trim();
      if (!q) {
        setDynamicTickers([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await api<TickerSearchResponse>("GET", `/api/tickers/search?query=${q}`);
        const results = (data.results || []).map((s) => ({
          ticker: s.ticker,
          name: s.name
        }));
        setDynamicTickers(results);
      } catch {
        // Search suggestions are opportunistic; keep the header usable on failure.
      } finally {
        setIsSearching(false);
      }
    };

    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [query, api]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset highlight when searching or list changes
  useEffect(() => { setHighlightIdx(0); }, [dynamicTickers.length]);

  const backTarget =
    typeof location.state?.from === "string" && location.state.from.trim()
      ? location.state.from
      : "/portal/overview";

  const selectTicker = (symbol: string) => {
    setQuery("");
    setOpen(false);
    navigate(`/ticker/${symbol}`, { state: { from: backTarget } });
  };

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <header className="px-3 md:px-7" style={{
      flexShrink: 0, background: T.surface,
      borderBottom: `1px solid ${T.border}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      height: 52, zIndex: 100, gap: "10px"
    }}>
      {/* Left: Back link + Logo + Search */}
      <div className="flex items-center gap-2 md:gap-4 shrink-0 min-w-0">
        {/* Logo */}
        <BrandLogo showWordmark={false} size={30} />

        {/* Back to Portal breadcrumb */}
        <button
          type="button"
          className="whitespace-nowrap"
          style={{
            textDecoration: "none",
            fontFamily: T.mono,
            fontSize: ".6rem",
            color: "var(--v2-text-muted)",
            letterSpacing: ".04em",
            transition: "color .15s",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
          onClick={() => navigate(backTarget)}
          onMouseEnter={(e) => (e.currentTarget.style.color = T.primary)}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--v2-text-muted)")}
        >
          &larr; Back to app
        </button>

        {/* Divider */}
        <span className="w-px h-5 shrink-0" style={{ background: "var(--v2-border)" }} />

        {/* Ticker Search */}
        <div ref={wrapperRef} style={{ position: "relative" }}>
          <div className="flex items-center gap-1.5 sm:gap-[6px] w-[140px] sm:w-[220px]" style={{
            background: "var(--v2-bg-surface)", border: "1px solid var(--v2-border)",
            borderRadius: 100, padding: "4px 12px",
          }}>
            {isSearching ? (
              <Loader2 size={13} className="animate-spin" color={T.primary} style={{ flexShrink: 0 }} />
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--v2-text-muted)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            )}
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value.toUpperCase()); setOpen(true); }}
              onFocus={() => { if (query.trim()) setOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setOpen(false); setQuery(""); (e.target as HTMLInputElement).blur(); }
                if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((i) => Math.min(i + 1, Math.max(0, dynamicTickers.length - 1))); }
                if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((i) => Math.max(i - 1, 0)); }
                if (e.key === "Enter" && dynamicTickers.length > 0) { e.preventDefault(); selectTicker(dynamicTickers[highlightIdx].ticker); }
              }}
              placeholder="Search ticker..."
              style={{
                background: "transparent", border: "none", outline: "none",
                color: "var(--v2-text-primary)", fontFamily: T.mono, fontSize: ".7rem",
                width: "100%", letterSpacing: ".04em",
              }}
            />
          </div>

          {/* Dropdown */}
          {open && dynamicTickers.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0,
              width: 250, maxHeight: 250, overflowY: "auto",
              background: "var(--v2-bg-panel)", border: "1px solid var(--v2-border)",
              borderRadius: 8, zIndex: 999,
              boxShadow: "0 8px 24px rgba(0,0,0,.2)",
            }}>
              {dynamicTickers.map((item, i) => (
                <div
                  key={item.ticker}
                  onMouseDown={() => selectTicker(item.ticker)}
                  onMouseEnter={() => setHighlightIdx(i)}
                  style={{
                    padding: "7px 14px", cursor: "pointer",
                    background: i === highlightIdx ? "var(--v2-bg-hover)" : "transparent",
                    transition: "background .1s",
                    display: "flex", flexDirection: "column", gap: 2,
                  }}
                >
                  <span style={{ fontFamily: T.mono, fontSize: ".75rem", color: "var(--v2-text-primary)", letterSpacing: ".06em" }}>{item.ticker}</span>
                  {item.name && (
                    <span style={{ fontFamily: T.mono, fontSize: ".55rem", color: "var(--v2-text-muted)", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {item.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Market Status + User Avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className="hidden md:flex" style={{ alignItems: "center", gap: 7, background: T.card2, borderRadius: 100, padding: "5px 12px", border: `1px solid ${T.border}` }}>
          <StatDot active />
          <Mono size=".57rem">Market Open</Mono>
          <span style={{ width: 1, height: 10, background: T.borderSoft, display: "inline-block" }} />
          <Mono size=".57rem" color={T.text}>
            {time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: getTimeFormat() === "12h", timeZone: getTimezone() })}
          </Mono>
        </div>
        
        <Link to="/portal" style={{ textDecoration: "none" }} title="Back to Portal">
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: `linear-gradient(135deg,${T.primary},#005f8a)`,
            display: "grid", placeItems: "center",
            fontFamily: T.display, fontSize: ".65rem", color: "white",
            cursor: "pointer", flexShrink: 0,
          }}>{(() => { const n = profile?.name?.trim(); if (!n) return "LH"; const parts = n.split(/\s+/); return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : n.slice(0, 2).toUpperCase(); })()}</div>
        </Link>
      </div>
    </header>
  );
};

export default AppHeader;
